// -- [Short URL validation for /try using the normal short-link redirect endpoint]

export type ShortUrlValidationStatus = 'valid' | 'invalid' | 'expired' | 'server_error' | 'network_error'

export interface ShortUrlValidationResult {
  status: ShortUrlValidationStatus
  message: string
  destination?: string
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function extractMessage(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return ''
  const obj = raw as Record<string, unknown>

  const directMessage = readString(obj.message)
  if (directMessage) return directMessage

  const errors = obj.errors
  if (errors && typeof errors === 'object') {
    const errorObj = errors as Record<string, unknown>
    if (errorObj.exception && typeof errorObj.exception === 'object') {
      const nested = errorObj.exception as Record<string, unknown>
      const nestedMessage = readString(nested.message)
      if (nestedMessage) return nestedMessage
    }
  }

  return ''
}

export function getShortUrlBaseUrl(): string {
  const envBase = readString(import.meta.env.VITE_SHORTURL_BASE_URL)
  if (envBase) return envBase.replace(/\/$/, '')
  return 'https://l.drivible.com'
}

function isDealerDomain(hostname: string): boolean {
  const host = hostname.toLowerCase()
  return (
    host === 'dealer.drivible.com.au' ||
    host === 'dealer.beta.drivible.com' ||
    host.endsWith('.dealer.drivible.com.au') ||
    host.endsWith('.dealer.beta.drivible.com')
  )
}

function isSpecialLoginDestination(destination: string): boolean {
  if (!destination) return false
  try {
    const parsed = new URL(destination)
    const hasInvitePath = parsed.pathname === '/register/invite'
    const hasResetToken = parsed.searchParams.has('passwordResetToken')
    return isDealerDomain(parsed.hostname) && hasInvitePath && hasResetToken
  } catch {
    return false
  }
}

function getValidRedirectMessage(destination: string): string {
  if (!destination) return 'Valid code. Redirecting now...'
  if (isSpecialLoginDestination(destination)) {
    return 'Redirecting you to the special login page now...'
  }
  return `Valid code. Redirecting you to ${destination}`
}

export async function validateShortCode(code: string): Promise<ShortUrlValidationResult> {
  const endpoint = `${getShortUrlBaseUrl()}/${encodeURIComponent(code)}`

  try {
    // -- We use redirect:manual so the browser does not leave /try before we render a status.
    const res = await fetch(endpoint, {
      method: 'GET',
      redirect: 'manual',
      credentials: 'omit',
      mode: 'cors',
    })

    let raw: unknown = null
    try {
      raw = await res.json()
    } catch {
      raw = null
    }

    const message = extractMessage(raw)
    const locationHeader = readString(res.headers.get('Location') ?? '')

    if (
      res.status === 200 ||
      res.status === 301 ||
      res.status === 302 ||
      res.status === 303 ||
      res.status === 307 ||
      res.status === 308
    ) {
      const redirectTarget = locationHeader || endpoint
      return {
        status: 'valid',
        message:
          message ||
          (locationHeader ? getValidRedirectMessage(locationHeader) : 'Valid code. Redirecting now...'),
        destination: redirectTarget,
      }
    }

    if (res.status === 404) {
      return {
        status: 'invalid',
        message: message || 'That code was not found. Please check and try again.',
      }
    }

    if (res.status === 403) {
      return {
        status: 'expired',
        message: message || 'That code is expired or inactive.',
      }
    }

    if (res.status >= 500) {
      return {
        status: 'server_error',
        message: message || 'Temporary server issue. Please try again later.',
      }
    }

    if (res.status === 400) {
      return {
        status: 'invalid',
        message: message || 'Invalid request.',
      }
    }

    if (res.type === 'opaqueredirect') {
      return {
        status: 'valid',
        message: 'Valid code. Redirecting now...',
        destination: endpoint,
      }
    }

    return {
      status: 'invalid',
      message: message || 'Unable to validate that code. Please try again.',
    }
  } catch (err: unknown) {
    // -- fetch() rejects when the response is not CORS-visible (common for cross-origin redirects
    // -- without a matching Access-Control-Allow-Origin). A full navigation to the short URL is not
    // -- subject to that read restriction, so we still send the user to the same place they would get from the link.
    const looksLikeBlockedRead =
      err instanceof TypeError &&
      /failed to fetch|networkerror|load failed|fetch/i.test(String((err as Error).message ?? ''))

    if (looksLikeBlockedRead) {
      return {
        status: 'valid',
        message:
          'Proceeding to your link. This page could not read the short-link response from the browser (often a cross-site policy on the short-link host). Opening the link will still redirect you correctly.',
        destination: endpoint,
      }
    }

    return {
      status: 'network_error',
      message: 'Network issue while checking that code. Please try again.',
    }
  }
}
